import type {
  CpqState,
  ShareStyleDefinition,
  ShareStyleSnapshot,
} from './types';
import { canManageGlobalSettings } from './viewGuard';

export const SHARE_STYLE_DEFINITION_KIND = 'share-style-definition-v1';
export const SHARE_STYLE_SNAPSHOT_KIND = 'share-style-snapshot-v1';

const STYLE_ID_PATTERN = /^[A-Z0-9][A-Z0-9_-]{1,63}$/;
const HEX_COLOR_PATTERN = /^#[0-9a-fA-F]{6}$/;
// SVG is deliberately excluded: even as an image data URL it is an executable
// document surface. The first release accepts only raster data URLs or
// ordinary HTTP(S) image URLs.
const IMAGE_DATA_PATTERN = /^data:image\/(?:png|jpe?g|gif|webp);/i;
const STYLE_NAME_LIMIT = 80;

export interface ShareStyleDraft {
  name: string;
  enabled: boolean;
  isDefault: boolean;
  coverImage: string;
  logoImage: string;
  themeColor: string;
  titleFontSize: number;
  titleBackground: string;
}

function isIsoTimestamp(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  try {
    return new Date(value).toISOString() === value;
  } catch {
    return false;
  }
}

export function isSafeShareStyleImage(value: unknown): value is string {
  if (typeof value !== 'string' || value.length > 2_000_000) return false;
  if (value === '') return true;
  if (IMAGE_DATA_PATTERN.test(value)) return true;
  try {
    const url = new URL(value);
    return (
      (url.protocol === 'https:' || url.protocol === 'http:') &&
      !url.username &&
      !url.password
    );
  } catch {
    return false;
  }
}

export function isValidShareStyleDefinition(
  value: unknown,
): value is ShareStyleDefinition {
  if (!value || typeof value !== 'object') return false;
  const style = value as Partial<ShareStyleDefinition>;
  return (
    style.kind === SHARE_STYLE_DEFINITION_KIND &&
    typeof style.id === 'string' &&
    STYLE_ID_PATTERN.test(style.id) &&
    typeof style.name === 'string' &&
    style.name.trim().length > 0 &&
    style.name.length <= STYLE_NAME_LIMIT &&
    typeof style.enabled === 'boolean' &&
    typeof style.isDefault === 'boolean' &&
    isSafeShareStyleImage(style.coverImage) &&
    isSafeShareStyleImage(style.logoImage) &&
    typeof style.themeColor === 'string' &&
    HEX_COLOR_PATTERN.test(style.themeColor) &&
    typeof style.titleFontSize === 'number' &&
    Number.isInteger(style.titleFontSize) &&
    style.titleFontSize >= 12 &&
    style.titleFontSize <= 48 &&
    typeof style.titleBackground === 'string' &&
    HEX_COLOR_PATTERN.test(style.titleBackground) &&
    typeof style.revision === 'number' &&
    Number.isInteger(style.revision) &&
    style.revision >= 1 &&
    isIsoTimestamp(style.updatedAt)
  );
}

export function isValidShareStyleSnapshot(
  value: unknown,
): value is ShareStyleSnapshot {
  if (!value || typeof value !== 'object') return false;
  const snapshot = value as Partial<ShareStyleSnapshot>;
  return (
    snapshot.kind === SHARE_STYLE_SNAPSHOT_KIND &&
    typeof snapshot.styleId === 'string' &&
    STYLE_ID_PATTERN.test(snapshot.styleId) &&
    typeof snapshot.name === 'string' &&
    snapshot.name.trim().length > 0 &&
    snapshot.name.length <= STYLE_NAME_LIMIT &&
    typeof snapshot.isDefault === 'boolean' &&
    isSafeShareStyleImage(snapshot.coverImage) &&
    isSafeShareStyleImage(snapshot.logoImage) &&
    typeof snapshot.themeColor === 'string' &&
    HEX_COLOR_PATTERN.test(snapshot.themeColor) &&
    typeof snapshot.titleFontSize === 'number' &&
    Number.isInteger(snapshot.titleFontSize) &&
    snapshot.titleFontSize >= 12 &&
    snapshot.titleFontSize <= 48 &&
    typeof snapshot.titleBackground === 'string' &&
    HEX_COLOR_PATTERN.test(snapshot.titleBackground) &&
    typeof snapshot.revision === 'number' &&
    Number.isInteger(snapshot.revision) &&
    snapshot.revision >= 1 &&
    isIsoTimestamp(snapshot.frozenAt)
  );
}

/** Historical snapshots may use a disabled style, but never an unknown ID. */
export function isKnownShareStyleSnapshot(
  state: Pick<CpqState, 'shareStyles'>,
  value: unknown,
): value is ShareStyleSnapshot {
  if (!isValidShareStyleSnapshot(value)) return false;
  try {
    validateShareStyleCatalog(state.shareStyles);
  } catch {
    return false;
  }
  return state.shareStyles.some((style) => style.id === value.styleId);
}

export function validateShareStyleCatalog(
  styles: unknown,
): asserts styles is ShareStyleDefinition[] {
  if (!Array.isArray(styles) || styles.length === 0) {
    throw new Error('分享样式目录不能为空');
  }
  const ids = new Set<string>();
  const defaults = styles.filter((style) => style?.isDefault);
  for (const style of styles) {
    if (!isValidShareStyleDefinition(style)) {
      throw new Error('分享样式定义无效');
    }
    if (ids.has(style.id)) throw new Error('分享样式标识重复');
    ids.add(style.id);
    if (style.isDefault && !style.enabled) {
      throw new Error('停用样式不能作为默认样式');
    }
  }
  if (defaults.length !== 1)
    throw new Error('分享样式必须且只能有一个默认样式');
}

export function isValidShareStyleCatalog(
  value: unknown,
): value is ShareStyleDefinition[] {
  try {
    validateShareStyleCatalog(value);
    return true;
  } catch {
    return false;
  }
}

const DEFAULT_STYLE_TIME = '2026-08-11T10:00:00.000Z';

export function createDefaultShareStyles(
  updatedAt = DEFAULT_STYLE_TIME,
): ShareStyleDefinition[] {
  return [
    {
      kind: SHARE_STYLE_DEFINITION_KIND,
      id: 'STYLE-DEFAULT',
      name: '默认表格',
      enabled: true,
      isDefault: true,
      coverImage: '',
      logoImage: '',
      themeColor: '#1677FF',
      titleFontSize: 24,
      titleBackground: '#EAF3FF',
      revision: 1,
      updatedAt,
    },
    {
      kind: SHARE_STYLE_DEFINITION_KIND,
      id: 'STYLE-MIDNIGHT',
      name: '深夜蓝图',
      enabled: true,
      isDefault: false,
      coverImage: '',
      logoImage: '',
      themeColor: '#0F3D66',
      titleFontSize: 22,
      titleBackground: '#E9F2F8',
      revision: 1,
      updatedAt,
    },
  ];
}

/** Hydrate a v5 directory strictly; older envelopes receive explicit defaults. */
export function hydrateShareStyles(
  raw: unknown,
  sourceVersion: 3 | 4 | 5 = 5,
): ShareStyleDefinition[] {
  if (!Array.isArray(raw)) {
    if (sourceVersion === 5) throw new Error('v5 缺少分享样式目录');
    return createDefaultShareStyles();
  }
  validateShareStyleCatalog(raw);
  return structuredClone(raw);
}

export function activeShareStyles(
  state: Pick<CpqState, 'shareStyles'>,
): ShareStyleDefinition[] {
  validateShareStyleCatalog(state.shareStyles);
  return structuredClone(
    state.shareStyles
      .filter((style) => style.enabled)
      .sort(
        (left, right) =>
          Number(right.isDefault) - Number(left.isDefault) ||
          left.name.localeCompare(right.name, 'zh-CN'),
      ),
  );
}

export function resolveEnabledShareStyle(
  state: Pick<CpqState, 'shareStyles'>,
  styleId?: string,
): ShareStyleDefinition {
  const active = activeShareStyles(state);
  const resolved = styleId
    ? active.find((style) => style.id === styleId)
    : active.find((style) => style.isDefault);
  if (!resolved) {
    throw new Error(
      styleId ? '分享样式不存在或已停用' : '分享样式缺少启用的默认样式',
    );
  }
  return resolved;
}

export function createShareStyleSnapshot(
  style: ShareStyleDefinition,
  frozenAt = new Date().toISOString(),
): ShareStyleSnapshot {
  if (!isValidShareStyleDefinition(style)) throw new Error('分享样式定义无效');
  if (!style.enabled) throw new Error('停用分享样式不能被选择');
  if (!isIsoTimestamp(frozenAt)) throw new Error('分享样式冻结时间无效');
  return {
    kind: SHARE_STYLE_SNAPSHOT_KIND,
    styleId: style.id,
    name: style.name,
    isDefault: style.isDefault,
    coverImage: style.coverImage,
    logoImage: style.logoImage,
    themeColor: style.themeColor,
    titleFontSize: style.titleFontSize,
    titleBackground: style.titleBackground,
    revision: style.revision,
    frozenAt,
  };
}

export function sameShareStyleSnapshot(
  left: ShareStyleSnapshot,
  right: ShareStyleSnapshot,
): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

export function sameShareStyleSnapshotFacts(
  left: ShareStyleSnapshot,
  right: ShareStyleSnapshot,
): boolean {
  const { frozenAt: _leftFrozenAt, ...leftFacts } = left;
  const { frozenAt: _rightFrozenAt, ...rightFacts } = right;
  return JSON.stringify(leftFacts) === JSON.stringify(rightFacts);
}

/** Read only already-published facts; never look the style up by current ID. */
export function frozenShareStyleForQuote(
  state: Pick<CpqState, 'publications' | 'shareStyles'>,
  quoteId: string,
  quoteVersion: number,
): ShareStyleSnapshot | undefined {
  const publications = (state.publications ?? []).filter(
    (publication) =>
      publication.quoteId === quoteId &&
      publication.quoteVersion === quoteVersion,
  );
  if (publications.length === 0) return undefined;
  const snapshots = publications.map(
    (publication) => publication.shareStyleSnapshot,
  );
  if (
    snapshots.some((snapshot) => !isKnownShareStyleSnapshot(state, snapshot))
  ) {
    throw new Error('历史发布缺少合法的分享样式快照');
  }
  const first = snapshots[0] as ShareStyleSnapshot;
  if (
    snapshots.some(
      (snapshot) =>
        !sameShareStyleSnapshot(first, snapshot as ShareStyleSnapshot),
    )
  ) {
    throw new Error('同一报价版本存在冲突的分享样式快照');
  }
  return structuredClone(first);
}

export function resolveShareStyleSnapshotForExport(
  state: CpqState,
  quoteId: string,
  quoteVersion: number,
  temporaryStyleId?: string,
  frozenAt = new Date().toISOString(),
): ShareStyleSnapshot {
  const published = frozenShareStyleForQuote(state, quoteId, quoteVersion);
  return (
    published ??
    createShareStyleSnapshot(
      resolveEnabledShareStyle(state, temporaryStyleId),
      frozenAt,
    )
  );
}

function assertCanManageShareStyles(state: CpqState): void {
  if (!canManageGlobalSettings(state))
    throw new Error('当前成员无权管理分享样式');
}

function validateDraft(draft: ShareStyleDraft): void {
  if (!draft.name.trim() || draft.name.length > STYLE_NAME_LIMIT) {
    throw new Error('分享样式名称不能为空且不得超过 80 个字符');
  }
  if (!draft.enabled && draft.isDefault)
    throw new Error('停用样式不能作为默认样式');
  if (
    !isSafeShareStyleImage(draft.coverImage) ||
    !isSafeShareStyleImage(draft.logoImage)
  ) {
    throw new Error('封面或 Logo 仅支持图片 data URL 或 HTTP(S) URL');
  }
  if (
    !HEX_COLOR_PATTERN.test(draft.themeColor) ||
    !HEX_COLOR_PATTERN.test(draft.titleBackground)
  ) {
    throw new Error('主题色和标题背景必须是六位十六进制颜色');
  }
  if (
    !Number.isInteger(draft.titleFontSize) ||
    draft.titleFontSize < 12 ||
    draft.titleFontSize > 48
  ) {
    throw new Error('标题字号必须是 12 到 48 的整数');
  }
}

function nextStyleRevision(style: ShareStyleDefinition): ShareStyleDefinition {
  return {
    ...style,
    revision: style.revision + 1,
    updatedAt: new Date().toISOString(),
  };
}

function enforceDefault(styles: ShareStyleDefinition[]): void {
  validateShareStyleCatalog(styles);
}

export function createShareStyle(
  state: CpqState,
  draft: ShareStyleDraft,
): CpqState {
  assertCanManageShareStyles(state);
  validateDraft(draft);
  if (state.shareStyles.some((style) => style.name === draft.name)) {
    throw new Error('分享样式名称已存在');
  }
  const next = structuredClone(state);
  const style: ShareStyleDefinition = {
    kind: SHARE_STYLE_DEFINITION_KIND,
    id: `STYLE-${String(next.nextSequence).padStart(4, '0')}`,
    ...draft,
    revision: 1,
    updatedAt: new Date().toISOString(),
  };
  next.nextSequence += 1;
  next.shareStyles = style.isDefault
    ? [
        ...next.shareStyles.map((item) => ({ ...item, isDefault: false })),
        style,
      ]
    : [...next.shareStyles, style];
  enforceDefault(next.shareStyles);
  return next;
}
export function updateShareStyle(
  state: CpqState,
  styleId: string,
  draft: ShareStyleDraft,
): CpqState {
  assertCanManageShareStyles(state);
  validateDraft(draft);
  const target = state.shareStyles.find((style) => style.id === styleId);
  if (!target) throw new Error('分享样式不存在');
  if (
    state.shareStyles.some(
      (style) => style.id !== styleId && style.name === draft.name,
    )
  ) {
    throw new Error('分享样式名称已存在');
  }
  if (target.isDefault && !draft.isDefault) {
    throw new Error('请先指定新的默认分享样式');
  }
  const next = structuredClone(state);
  const updated = nextStyleRevision({ ...target, ...draft });
  next.shareStyles = next.shareStyles.map((style) =>
    style.id === styleId
      ? updated
      : draft.isDefault
        ? { ...style, isDefault: false }
        : style,
  );
  enforceDefault(next.shareStyles);
  return next;
}

export function setShareStyleEnabled(
  state: CpqState,
  styleId: string,
  enabled: boolean,
): CpqState {
  assertCanManageShareStyles(state);
  const target = state.shareStyles.find((style) => style.id === styleId);
  if (!target) throw new Error('分享样式不存在');
  if (!enabled && target.isDefault)
    throw new Error('默认分享样式不能停用，请先指定新的默认样式');
  const next = structuredClone(state);
  next.shareStyles = next.shareStyles.map((style) =>
    style.id === styleId ? nextStyleRevision({ ...style, enabled }) : style,
  );
  enforceDefault(next.shareStyles);
  return next;
}

export function setDefaultShareStyle(
  state: CpqState,
  styleId: string,
): CpqState {
  assertCanManageShareStyles(state);
  const target = resolveEnabledShareStyle(state, styleId);
  const next = structuredClone(state);
  next.shareStyles = next.shareStyles.map((style) =>
    style.id === target.id
      ? nextStyleRevision({ ...style, isDefault: true })
      : style.isDefault
        ? nextStyleRevision({ ...style, isDefault: false })
        : style,
  );
  enforceDefault(next.shareStyles);
  return next;
}
